import { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, Copy, ShieldCheck, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const load = () =>
    api.get("/staff").then((r) => setStaff(r.data)).catch((e) => {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(formatApiError(e));
    });
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/staff", form);
      setTempPassword({ name: data.name, email: data.email, password: data.temp_password });
      setDialogOpen(false);
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/staff/${deleting.id}`);
      toast.success("Utente eliminato");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReset = async (u) => {
    try {
      const { data } = await api.post(`/staff/${u.id}/reset-password`);
      setTempPassword({ name: u.name, email: u.email, password: data.temp_password });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword.password);
    toast.success("Password copiata");
  };

  if (forbidden) {
    return (
      <AdminLayout title="Utenti Studio" subtitle="Gestione accessi dello studio">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800" data-testid="staff-forbidden">
          Questa sezione è riservata al super amministratore.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Utenti Studio" subtitle="Account dello studio con accesso all'area amministrativa">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">{staff ? `${staff.length} utenti studio` : "Caricamento..."}</p>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-staff-button" className="bg-navy hover:bg-navy-light text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Utente Studio
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        {staff === null ? (
          <p className="text-sm text-muted-foreground p-12 text-center">Caricamento...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100 bg-slate-50/60">
                  <th className="px-6 py-3 font-semibold">Nome</th>
                  <th className="px-6 py-3 font-semibold">Email</th>
                  <th className="px-6 py-3 font-semibold">Ruolo</th>
                  <th className="px-6 py-3 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {staff.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/60 transition-colors duration-150" data-testid={`staff-row-${u.email}`}>
                    <td className="px-6 py-4 font-medium text-navy">{u.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{u.email}</td>
                    <td className="px-6 py-4">
                      {u.is_super ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-[#3C763D] border border-green-200" data-testid={`staff-super-badge-${u.email}`}>
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Super Amministratore
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                          <UserCog className="w-3.5 h-3.5" />
                          Operatore
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleReset(u)} title="Reimposta password" data-testid={`staff-reset-${u.email}`}>
                          <KeyRound className="w-4 h-4 text-[#3C763D]" />
                        </Button>
                        {!u.is_super && (
                          <Button variant="ghost" size="sm" onClick={() => setDeleting(u)} title="Elimina" data-testid={`staff-delete-${u.email}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Nuovo Utente Studio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="s-name">Nome e cognome</Label>
              <Input id="s-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-email">Email</Label>
              <Input id="s-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" />
            </div>
            <p className="text-xs text-muted-foreground">L'utente avrà accesso completo all'area amministrativa (clienti, F24, reminder). Solo il super amministratore può gestire gli utenti studio.</p>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="staff-save-button" className="bg-navy hover:bg-navy-light text-white">
                {saving ? "Creazione..." : "Crea account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent data-testid="staff-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>L'account non potrà più accedere all'area amministrativa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="staff-delete-confirm" className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent data-testid="staff-temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Credenziali di accesso</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Comunica queste credenziali a <strong>{tempPassword?.name}</strong>. La password temporanea non sarà più visibile dopo la chiusura.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 font-mono text-sm">
            <p><span className="text-muted-foreground font-sans">Email:</span> {tempPassword?.email}</p>
            <div className="flex items-center justify-between gap-2">
              <p className="truncate"><span className="text-muted-foreground font-sans">Password:</span> <strong data-testid="staff-temp-password-value">{tempPassword?.password}</strong></p>
              <Button variant="outline" size="sm" onClick={copyPassword} data-testid="staff-copy-password-button">
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copia
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)} data-testid="staff-temp-password-close" className="bg-navy hover:bg-navy-light text-white">Ho annotato le credenziali</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
